<script setup>
import { computed, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import LabelItem from 'dashboard/components-next/label/LabelItem.vue';
import AddLabel from 'dashboard/components-next/label/AddLabel.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    default: null,
  },
});

const store = useStore();

const allLabels = useMapGetter('labels/getLabels');
const contactLabels = useMapGetter('contactLabels/getContactLabels');

const savedLabels = computed(() => {
  const availableContactLabels = contactLabels.value(props.contactId) || [];
  const labels = Array.isArray(allLabels.value) ? allLabels.value : [];

  return labels.filter(({ title }) => availableContactLabels.includes(title));
});

const labelMenuItems = computed(() => {
  const labels = Array.isArray(allLabels.value) ? allLabels.value : [];

  const items = labels.map(label => ({
    label: label.title,
    value: label.id,
    thumbnail: { name: label.title, color: label.color },
    isSelected: savedLabels.value.some(
      savedLabel => savedLabel.id === label.id
    ),
    action: 'contactLabel',
  }));

  return items.sort((a, b) => Number(a.isSelected) - Number(b.isSelected));
});

const fetchLabels = async contactId => {
  if (!contactId) {
    return;
  }

  await Promise.all([
    store.dispatch('labels/get'),
    store.dispatch('contactLabels/get', contactId),
  ]);
};

const handleLabelAction = async ({ value }) => {
  try {
    const currentLabels = savedLabels.value.map(label => label.title);
    const labels = Array.isArray(allLabels.value) ? allLabels.value : [];
    const selectedLabel = labels.find(label => label.id === value);

    if (!selectedLabel) {
      return;
    }

    const updatedLabels = currentLabels.includes(selectedLabel.title)
      ? currentLabels.filter(labelTitle => labelTitle !== selectedLabel.title)
      : [...currentLabels, selectedLabel.title];

    await store.dispatch('contactLabels/update', {
      contactId: props.contactId,
      labels: updatedLabels,
    });
  } catch (error) {
    // Chatwoot store already owns the user-facing error flow here.
  }
};

const handleRemoveLabel = label => {
  return handleLabelAction({ value: label.id });
};

watch(
  () => props.contactId,
  contactId => {
    fetchLabels(contactId);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <LabelItem
      v-for="label in savedLabels"
      :key="label.id"
      :label="label"
      :is-hovered="true"
      @remove="handleRemoveLabel"
    />
    <AddLabel
      :label-menu-items="labelMenuItems"
      @update-label="handleLabelAction"
    />
  </div>
</template>
